import { getPropertyDisplayTitle, getPropertyPartialTask, getPropertyValueFromTask } from "~/common/properties";
import { COMPLETED_STATUS_KINDS } from "~/components/visualization/constants";
import { isDefined } from "~/shared/common";
import { StatusKind } from "~/shared/enums";
import type { Property, Status, StatusUpdate, TaskKind, TaskUpdate } from "~/shared/types";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid } from "~/utils/common";
import { statusComparator } from "~/utils/comparator";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";
import { removeFromSortedMap, setObjectMapAndSortedMapGrouped, updateInSortedMap } from "./utils";

export type State = {
  _duidsToStatuses: Map<string, Status>;
  _sortedStatusDuidsByPropertyDuid: Map<string, string[]>;
};

export const state: State = {
  _duidsToStatuses: new Map(),
  _sortedStatusDuidsByPropertyDuid: new Map(),
};

export type Getters = {
  getStatusList: (property: Property, taskKinds?: TaskKind[]) => Status[];
  getStatusByDuid: (duid: string) => Status | undefined;
  getStatusByTitle: (title: string, property: Property) => Status | undefined;
  getStatusesByKinds: (kinds: StatusKind[], property: Property) => Status[];
  defaultUnstartedStatus: (property: Property) => Status;
  defaultDefaultUnstartedStatus: Status;
  defaultDefaultFinishedStatus: Status;
  defaultCompletedStatusDuids: Set<string>;
};

export type Actions = {
  createStatus: (
    kind: StatusKind,
    property: Property,
    order: string,
    partialStatus?: Partial<Status>,
    options?: { awaitBackend?: boolean }
  ) => Promise<Status>;
  updateStatus: (update: StatusUpdate, options?: { awaitBackend?: boolean }) => Promise<Status | undefined>;
  deleteStatus: (status: Status, property: Property, options?: { awaitBackend?: boolean }) => Promise<void>;
  $setStatuses: (statuses: Status[]) => void;
  $createOrUpdateStatus: (status: Status) => void;
  $deleteStatus: (status: Status) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getStatusList() {
    return (property, taskKinds) => {
      const hiddenStatusDuids = new Set(taskKinds ? taskKinds.flatMap((e) => e.hiddenStatusDuids) : []);
      return (this._sortedStatusDuidsByPropertyDuid.get(property.duid) ?? [])
        .map((e) => this._duidsToStatuses.get(e))
        .filter(isDefined)
        .filter((e) => !hiddenStatusDuids.has(e.duid));
    };
  },
  getStatusByDuid() {
    return (duid) => this._duidsToStatuses.get(duid);
  },
  getStatusByTitle() {
    return (title, property) => this.getStatusList(property).find((e) => title === e.title);
  },
  getStatusesByKinds() {
    return (kinds, property) => this.getStatusList(property).filter((e) => kinds.includes(e.kind));
  },
  defaultUnstartedStatus() {
    return (property) => {
      const status = this.getStatusList(property).filter((e) => e.locked && e.kind === StatusKind.UNSTARTED);
      if (!status) {
        throw new Error(
          `Expected exactly one default unstarted status for property ${getPropertyDisplayTitle(property)}`
        );
      }
      return status[0];
    };
  },
  defaultDefaultUnstartedStatus() {
    return this.defaultUnstartedStatus(this.defaultStatusProperty);
  },
  defaultDefaultFinishedStatus() {
    const status = this.getStatusList(this.defaultStatusProperty).filter(
      (e) => e.locked && e.kind === StatusKind.FINISHED
    );
    if (!status) {
      throw new Error(
        `Expected exactly one default finished status for property ${getPropertyDisplayTitle(this.defaultStatusProperty)}`
      );
    }
    return status[0];
  },
  defaultCompletedStatusDuids() {
    return new Set(this.getStatusesByKinds([...COMPLETED_STATUS_KINDS], this.defaultStatusProperty).map((e) => e.duid));
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async createStatus(kind, property, order, partialStatus, options = {}) {
    const { awaitBackend = false } = options;
    const status: Status = {
      duid: makeDuid(),
      propertyDuid: property.duid,
      kind,
      locked: false,
      order,
      title: "",
      colorHex: makeRandomColorHex(),
      description: "",
      ...partialStatus,
    };

    this.$createOrUpdateStatus(status);

    const backendAction = this.$backend.status.create(status);
    if (awaitBackend) {
      await backendAction;
    }
    return status;
  },
  async updateStatus(update, options = {}) {
    const { awaitBackend = false } = options;
    const status = this.getStatusByDuid(update.duid);
    if (!status) {
      return undefined;
    }

    Object.assign(status, update);
    updateInSortedMap(
      this._duidsToStatuses,
      this._sortedStatusDuidsByPropertyDuid,
      status.propertyDuid,
      status.duid,
      () => statusComparator
    );

    const backendAction = this.$backend.status.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return status;
  },
  async deleteStatus(status, property, options = {}) {
    const { awaitBackend = false } = options;

    const isCompleted = COMPLETED_STATUS_KINDS.has(status.kind);
    let updates: TaskUpdate[];
    if (property.duid === this.defaultStatusProperty.duid) {
      const newStatusDuid = isCompleted
        ? this.defaultDefaultFinishedStatus.duid
        : this.defaultDefaultUnstartedStatus.duid;
      const tasksWithStatus = this.getAllLoadedTasksForUiOnly({ includeTrashed: true, includeDraft: true }).filter(
        (e) => e.statusDuid === status.duid
      );
      updates = tasksWithStatus.map((e) => ({ duid: e.duid, statusDuid: newStatusDuid }));
    } else {
      const newStatusDuid = this.getStatusesByKinds(
        [isCompleted ? StatusKind.FINISHED : StatusKind.UNSTARTED],
        property
      )[0]?.duid;
      const tasksWithStatus = this.getAllLoadedTasksForUiOnly({ includeTrashed: true, includeDraft: true }).filter(
        (task) => getPropertyValueFromTask(property, task) === status.duid
      );
      updates = tasksWithStatus.map((e) => ({ duid: e.duid, ...getPropertyPartialTask(property, e, newStatusDuid) }));
    }

    if (updates.length > 0) {
      this.updateTasks(updates, { noUndo: true, noCelebrate: true, noBackend: true });
    }

    this.$cleanupFormFieldsDefaultsNoBackend([status.duid], [status.propertyDuid]);
    this.$cleanupLayoutsFiltersNoBackend([status.duid], [status.propertyDuid]);

    this.$deleteStatus(status);

    const backendAction = this.$backend.status.delete(status.duid);
    if (awaitBackend) {
      await backendAction;
    }
  },
  $setStatuses(statuses) {
    setObjectMapAndSortedMapGrouped(
      this._duidsToStatuses,
      this._sortedStatusDuidsByPropertyDuid,
      statuses,
      () => statusComparator
    );
  },
  $createOrUpdateStatus(status) {
    const currentStatus = this.getStatusByDuid(status.duid);

    if (!currentStatus) {
      this._duidsToStatuses.set(status.duid, status);
    } else {
      Object.assign(currentStatus, status);
    }
    updateInSortedMap(
      this._duidsToStatuses,
      this._sortedStatusDuidsByPropertyDuid,
      status.propertyDuid,
      status.duid,
      () => statusComparator
    );
  },
  $deleteStatus(status) {
    this._duidsToStatuses.delete(status.duid);
    removeFromSortedMap(this._sortedStatusDuidsByPropertyDuid, status.propertyDuid, status.duid);
  },
};

export { actions, getters };
